'use client';

// AddMoreContentPrompt - Inline prompt to add content when bot can't answer

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslations } from 'next-intl';
import type { ContentType } from './types';

// Re-export for consumers
export type { ContentType };

export interface AddMoreContentPromptProps {
  /** Callback when user wants to add content */
  onAddContent: (type: ContentType) => void;
}

/**
 * AddMoreContentPrompt - Inline prompt to add more content
 *
 * Shown when bot responds with "I don't have information about that"
 *
 * Features:
 * - 3 input options: Text, Document, URL
 * - Consistent icons with content-input components
 * - Compact inline design
 */
export function AddMoreContentPrompt({ onAddContent }: AddMoreContentPromptProps) {
  const t = useTranslations('creator.testing');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        p: 2,
        ml: { xs: 2, md: 5 }, // Align with assistant messages, less on mobile
        backgroundColor: 'info.50',
        borderRadius: 2,
        borderInlineStart: '3px solid',
        borderInlineStartColor: 'info.main',
        mb: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t('addContent.prompt')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TextSnippetIcon />}
          onClick={() => onAddContent('text')}
          sx={{ textTransform: 'none' }}
        >
          {t('addContent.text')}
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<InsertDriveFileIcon />}
          onClick={() => onAddContent('document')}
          sx={{ textTransform: 'none' }}
        >
          {t('addContent.document')}
        </Button>

        <Button
          variant="outlined"
          size="small"
          startIcon={<LinkIcon />}
          onClick={() => onAddContent('url')}
          sx={{ textTransform: 'none' }}
        >
          {t('addContent.url')}
        </Button>
      </Box>
    </Box>
  );
}

export default AddMoreContentPrompt;
